import React, {useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import constants from 'helpers/constants';
import DialogContent from 'components/atoms/Dialogs/DialogContent/DialogContent';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import DialogFooter from 'components/molecules/Dialogs/DialogFooter/DialogFooter';
import Button from 'components/atoms/Buttons/Button/Button';
import DialogHeader from 'components/molecules/Dialogs/DialogHeader/DialogHeader';
import {Span} from 'components/atoms/Text/Typography/Typography';
import utils from 'helpers/utils';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Close from '@mui/icons-material/Close';
import StyledEntitiesExportSettingsDialog
  from 'components/organisms/Dialogs/EntitiesExportSettingsDialog/EntitiesExportSettingsDialog.styles';
import Download from '@mui/icons-material/Download';

const EntitiesExportSettingsDialog = React.forwardRef((props, ref) => {
  const {
    count,
    onSubmit,
    ...innerProps
  } = useComponentProps(props, 'EntitiesExportSettingsDialog', {
    children: ['header', 'content', 'footer']
  });

  const innerRef = useRef(null);
  const formRef = useRef(null);

  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(null);
  const [validation, setValidation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useImperativeHandle(ref, () => innerRef.current);

  const fields = useMemo(() => {
    return [
      {
        name: 'columnSelection',
        label: 'Which columns do you want to download?',
        entity: 'entitiesExport',
        type: constants.formFieldTypes.list,
        validation: constants.formFieldValidationTypes.text,
        conversion: constants.formFieldConversionTypes.value,
        initial: 'selectedColumns',
        options: [
          {value: 'allColumns', label: 'All columns'},
          {value: 'selectedColumns', label: 'Selected columns'}
        ],
        FormFieldProps: {
          multiple: false,
          ListProps: {
            catchFocus: false
          }
        }
      }
    ]
  }, []);

  const handleSubmit = (values, actions) => {
    setSubmitting(true);

    utils.asPromise(onSubmit)(values)
      .then(() => {
        innerProps.onClose?.(null, 'saveButtonClick');
      })
      .catch(() => {
        setError('Download companies failed');
      })
      .finally(() => {
        actions.setSubmitting(false);
        setSubmitting(false);
      });
  };

  const handleValidating = (isValidating, isDirty, hasErrors) => {
    setDirty(isDirty);
    if (hasErrors) {
      setValidation('Please check if all fields have the correct values');
    } else {
      setValidation(null);
    }
  }

  const handleSubmitClick = () => {
    formRef.current?.submit();
  };

  const handleCancel = (e) => {
    innerRef.current?.close?.(e, 'cancelButtonClick');
  }

  const handleClose = (e, reason) => {
    if ((!submitting && !error && !dirty) || ['escapeKeyDown', 'closeButtonClick', 'cancelButtonClick'].includes(reason)) {
      innerProps.onClose?.(e, reason);
    }
  }

  const renderButtons = () => {
    return <React.Fragment>
      <Button children={'Cancel'}
              variant="text"
              startIcon={<Icon icon={Close}/>}
              onClick={handleCancel}/>
      <Button disabled={submitting}
              type="submit"
              color="primary"
              variant="contained"
              children="Download"
              startIcon={<Icon icon={Download}/>}
              onClick={handleSubmitClick}/>
    </React.Fragment>
  }

  return <StyledEntitiesExportSettingsDialog ref={innerRef} {...innerProps} onClose={handleClose}>
    <DialogHeader className="EntitiesExportSettingsDialog-header"
                  title="Download companies"
                  subtitle={`For ${utils.formatNumber(count)} compan${count === 1 ? 'y' : 'ies'}`}/>
    <DialogContent className="EntitiesExportSettingsDialog-content" >
      <InlineForm ref={formRef} fields={fields}
                  onValidating={handleValidating}
                  onSubmit={handleSubmit} />
    </DialogContent>
    <DialogFooter className="EntitiesExportSettingsDialog-footer"
                  info={(error ?? validation) ? <Span color="error">{error ?? validation}</Span> : null}
                  buttons={renderButtons()} />
  </StyledEntitiesExportSettingsDialog>
});

EntitiesExportSettingsDialog.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  count: PropTypes.numeric,
  onSubmit: PropTypes.func
};

export default EntitiesExportSettingsDialog;
